import React, { useId } from 'react';

export interface ContactorSvgProps {
  designator: string;
  isEnergized?: boolean;
  terminalValues?: Record<string, string>;
  highlightTerminals?: string[];
  activeTerminalId?: string | null | undefined;
  onTerminalClick?: (terminalId: string, e: React.MouseEvent) => void;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Endüstriyel 3 Kutuplu Güç Kontaktörü (Contactor) Vektörel Çizimi
 * DIN Rayına monte edilebilir, A1-A2 bobin, 3x Güç NO, 1x Yardımcı NO (13-14) klemensli.
 * Bobin enerjilendiğinde merkezdeki armatür mekanik olarak içeri çekilir (Plunger Actuation).
 */
export const ContactorSvg: React.FC<ContactorSvgProps> = ({
  designator = 'KM1',
  isEnergized = false,
  terminalValues = {},
  highlightTerminals = [],
  activeTerminalId = null,
  onTerminalClick,
  style,
  className = '',
}) => {
  const rawId = useId();
  const uid = rawId.replace(/[:]/g, '_');

  const width = 96;
  const height = 120;

  const getTerminalColor = (tid: string): string => {
    const pot = terminalValues[tid];
    if (pot === 'L1') return '#f59e0b';
    if (pot === 'L2') return '#ea580c';
    if (pot === 'L3') return '#cbd5e1';
    if (pot === 'N') return '#38bdf8';
    if (pot === 'PE') return '#84cc16';
    return '#94a3b8';
  };

  const renderScrewTerminal = (tid: string, label: string, cx: number, cy: number, isCoil = false) => {
    const isTarget = highlightTerminals.includes(tid);
    const isActive = activeTerminalId === tid;
    const termColor = getTerminalColor(tid);
    const hasPotential = terminalValues[tid] && terminalValues[tid] !== 'FLOATING';

    return (
      <g
        key={tid}
        transform={`translate(${cx}, ${cy})`}
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onTerminalClick?.(tid, e);
        }}
      >
        {/* Terminal Havşası */}
        <circle cx="0" cy="0" r="6.5" fill="#18202c" stroke="#334155" strokeWidth="1" />
        
        {/* Bobin terminalleri için pirinç renk vurgusu, diğerleri galvaniz */}
        <circle
          cx="0"
          cy="0"
          r="4.2"
          fill={isCoil ? '#d97706' : '#64748b'}
          stroke="#0f172a"
          strokeWidth="0.8"
        />

        {/* Vida kafası yarığı */}
        <line x1="-2.8" y1="-1.4" x2="2.8" y2="1.4" stroke="#0f172a" strokeWidth="1.2" />

        {/* Gerilim altındaysa renkli parlama halesi */}
        {hasPotential && (
          <circle cx="0" cy="0" r="7" fill="none" stroke={termColor} strokeWidth="1.5" strokeOpacity="0.8" />
        )}

        {/* Hedef veya Aktif bağlantı göstergesi */}
        {(isTarget || isActive) && (
          <circle
            cx="0"
            cy="0"
            r="9.5"
            fill="none"
            stroke="#38bdf8"
            strokeWidth="1.8"
            strokeDasharray={isActive ? 'none' : '3,2'}
            className="terminal-pulse"
          />
        )}

        {/* Klemens İsim Etiketi */}
        <text
          x="0"
          y={cy < 35 ? -8 : 13}
          fill="#cbd5e1"
          fontSize="6.8"
          fontFamily="system-ui, monospace"
          fontWeight="bold"
          textAnchor="middle"
        >
          {label}
        </text>
      </g>
    );
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`contactor-device-svg ${className}`}
      style={{ overflow: 'visible', userSelect: 'none', display: 'block', ...style }}
    >
      <defs>
        {/* Gövde Arduvaz Gri Plastik Gradyanı */}
        <linearGradient id={`contactorBodyGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="25%" stopColor="#1e293b" />
          <stop offset="85%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Hareketli Armatür Piston Gradyanı */}
        <linearGradient id={`armatureGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#c2410c" />
        </linearGradient>

        <filter id={`contactorShadow-${uid}`} x="-15%" y="-15%" width="130%" height="130%">
          <feDropShadow dx="0" dy="5" stdDeviation="5" floodColor="#000000" floodOpacity="0.4" />
        </filter>
      </defs>

      {/* Ana Gövde */}
      <g filter={`url(#contactorShadow-${uid})`}>
        <rect
          x="1"
          y="1"
          width={width - 2}
          height={height - 2}
          rx="5"
          fill={`url(#contactorBodyGrad-${uid})`}
          stroke="#475569"
          strokeWidth="1.2"
        />

        {/* Ön Panel Plakası (Açık tonlu ön yüzey) */}
        <rect
          x="6"
          y="28"
          width={width - 12}
          height="64"
          rx="3"
          fill="#f8fafc"
          stroke="#94a3b8"
          strokeWidth="1"
        />

        {/* İsim & Marka Etiketleri */}
        <rect x="12" y="32" width="28" height="12" rx="2" fill="#0284c7" />
        <text
          x="26"
          y="41"
          fill="#ffffff"
          fontSize="7"
          fontFamily="system-ui, sans-serif"
          fontWeight="bold"
          textAnchor="middle"
        >
          {designator}
        </text>

        <text
          x={width - 12}
          y="41"
          fill="#475569"
          fontSize="6"
          fontFamily="monospace"
          fontWeight="bold"
          textAnchor="end"
        >
          230V 50/60Hz
        </text>

        {/* Mekanik Armatür / Piston Çekme Göstergesi (Plunger Window) */}
        <rect
          x={width / 2 - 16}
          y="50"
          width="32"
          height="24"
          rx="2"
          fill="#1e293b"
          stroke="#334155"
          strokeWidth="1"
        />

        {/* Hareketli Manyetik Nüve Bloğu */}
        <g
          transform={`translate(${width / 2 - 12}, ${isEnergized ? 56 : 52})`}
          style={{ transition: 'transform 0.1s ease-in-out' }}
        >
          <rect
            x="0"
            y="0"
            width="24"
            height={isEnergized ? '14' : '18'}
            rx="2"
            fill={isEnergized ? `url(#armatureGrad-${uid})` : '#475569'}
            stroke="#0f172a"
            strokeWidth="0.8"
          />
          {/* Durum Yazısı / Simgesi */}
          <text
            x="12"
            y={isEnergized ? 10 : 12}
            fill="#ffffff"
            fontSize="6.5"
            fontFamily="system-ui, sans-serif"
            fontWeight="bold"
            textAnchor="middle"
          >
            {isEnergized ? 'I - ON' : 'O - OFF'}
          </text>
        </g>

        {/* Teknik Değerler Çizimi */}
        <text
          x={width / 2}
          y="84"
          fill="#64748b"
          fontSize="5.5"
          fontFamily="system-ui, sans-serif"
          textAnchor="middle"
        >
          AC-3: 400V 7.5kW 18A
        </text>
      </g>

      {/* Üst Klemens Sırası */}
      {renderScrewTerminal('A1', 'A1', 12, 14, true)}
      {renderScrewTerminal('1', '1/L1', 30, 14)}
      {renderScrewTerminal('3', '3/L2', 48, 14)}
      {renderScrewTerminal('5', '5/L3', 66, 14)}
      {renderScrewTerminal('13', '13 NO', 84, 14)}

      {/* Alt Klemens Sırası */}
      {renderScrewTerminal('2', '2/T1', 20, 106)}
      {renderScrewTerminal('4', '4/T2', 38, 106)}
      {renderScrewTerminal('6', '6/T3', 56, 106)}
      {renderScrewTerminal('14', '14 NO', 74, 106)}
      {renderScrewTerminal('A2', 'A2', 88, 106, true)}
    </svg>
  );
};
